import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { defaultLoanServicingStatusKeys } from 'config'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Tooltip } from 'stories/components'
import { Checkbox } from 'stories/components/Checkbox/Checkbox'
import { confirm, generateUniqeId, prompt } from 'utils'

interface ILoanServicingStatusConfig {
  title: string
  visible: boolean
  isIncludedCalculation?: boolean
  /** A loan in this status is still an active serviced loan (drafted, billed, in the grids). */
  isActive?: boolean
  /**
   * Tags stamped on / taken off the loan when it moves into this status. A converted book carries
   * the old servicer's status as a tag, and that tag is the chip under the loan number, so without
   * this a loan paid off in Nexys keeps reading as whatever it was at conversion.
   */
  addTags?: string[]
  removeTags?: string[]
}

const tagsToText = (tags?: string[]) => (tags || []).join(', ')
const textToTags = (value: string) =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)

export const LoanServicingStatus = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, ILoanServicingStatusConfig>>({})

  useEffect(() => {
    setLoading(true)
    getAdminConfig('loanServicingStatus')
      .then((data) => {
        const servicingStatusConfigData = cloneDeep(data)
        Object.keys(servicingStatusConfigData).forEach((key) => {
          const item = servicingStatusConfigData[key]
          if (item.isIncludedCalculation === undefined) {
            item.isIncludedCalculation = true
          }
        })
        setData(servicingStatusConfigData)
      })
      .finally(() => setLoading(false))
  }, [])

  const onSync = async (_data?: any) => {
    setLoading(true)

    setAdminConfig('loanServicingStatus', _data || data)
      .then(() => {})
      .finally(() => setLoading(false))
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data) as any
    if (temp[key] === undefined) temp[key] = {}
    temp[key][map] = value
    setData(temp)
    onSync(temp)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the new title')
    if (!title) return
    const newData = cloneDeep(data)
    const newKey = generateUniqeId(title, Object.keys(data))
    if (!newKey) return
    newData[newKey] = {
      title,
      visible: true,
      isIncludedCalculation: true,
    }
    setData(newData)
    onSync(newData)
  }

  const onRemove = async (key: string) => {
    const result = await confirm('Are you sure you want to remove this item?')
    if (!result) return
    const newData = cloneDeep(data)
    delete newData[key]
    setData(newData)
    onSync(newData)
  }

  const onChangeOrder = (toIndex: number, fromIndex: number) => {
    const keys = Object.keys(data)

    const newKeys = cloneDeep(keys)
    const [movedKey] = newKeys.splice(fromIndex, 1)
    newKeys.splice(toIndex, 0, movedKey)

    const newData: Record<string, ILoanServicingStatusConfig> = {}
    newKeys.forEach((key) => (newData[key] = data[key]))

    setData(newData)
    onSync(newData)
  }

  const sortKeys = Object.keys(Array(Object.keys(data).length).fill(1))

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center`}>
        <p>- Servicing Pipeline</p>
        <div className="flex">
          <span className="btn-icon" onClick={onAdd}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>
      </div>
      <div className="mt-4">
        <table className="text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {[
                { title: 'No' },
                { title: 'Title' },
                { title: 'In Use' },
                { title: 'Status' },
                { title: 'Active' },
                {
                  title: 'Tags Added',
                  tooltip: 'Tags stamped on the loan when it moves into this status (comma separated).',
                },
                {
                  title: 'Tags Removed',
                  tooltip:
                    'Tags taken off the loan when it moves into this status (comma separated). System tags are never removed.',
                },
                { title: 'Order' },
                { title: 'Action' },
              ].map(({ title, tooltip }) => (
                <th scope="col" className="p-3 border" key={`header-${title}`}>
                  {tooltip ? <Tooltip message={tooltip}>{title}</Tooltip> : title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => {
              const { title, visible, isIncludedCalculation, isActive, addTags, removeTags } = data[key]
              const isCustom = !defaultLoanServicingStatusKeys.includes(key)
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={title}
                      content={title}
                      onChange={(value: string) => onChange(key, value, 'title')}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Checkbox
                      color="gray"
                      id={key}
                      title={''}
                      key={key}
                      value={visible}
                      onChange={(value) => onChange(key, value, 'visible')}
                      className="w-[none]"
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Tooltip message="Included in Status Bubbles Calculation">
                      <Checkbox
                        color="blue"
                        id={`${key}-isIncludedCalculation`}
                        value={isIncludedCalculation ?? true}
                        onChange={(value) => onChange(key, value, 'isIncludedCalculation')}
                      />
                    </Tooltip>
                  </td>
                  <td className="px-3 border text-center">
                    <Tooltip message="Active loans are the ones included in ACH runs, statement runs, notices, and the default view of the servicing grids and reports. Turn this off for a status that closes a loan out, such as Paid in Full.">
                      <Checkbox
                        color="blue"
                        id={`${key}-isActive`}
                        value={isActive ?? true}
                        onChange={(value) => onChange(key, value, 'isActive')}
                      />
                    </Tooltip>
                  </td>
                  <td className="px-3 border min-w-[130px]">
                    <PlainInput
                      value={tagsToText(addTags)}
                      content={tagsToText(addTags) || '-'}
                      onChange={(value: string) => onChange(key, textToTags(value), 'addTags')}
                    />
                  </td>
                  <td className="px-3 border min-w-[130px]">
                    <PlainInput
                      value={tagsToText(removeTags)}
                      content={tagsToText(removeTags) || '-'}
                      onChange={(value: string) => onChange(key, textToTags(value), 'removeTags')}
                    />
                  </td>
                  <td className="px-2 border text-center">
                    <select
                      value={index}
                      className="text-sm py-1 border-gray-200 w-16"
                      onChange={(e) => onChangeOrder(parseInt(e.target.value), index)}
                    >
                      {sortKeys.map((value) => (
                        <option value={value} key={`option-${value}`}>
                          {parseInt(value) + 1}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 border">
                    {isCustom && (
                      <div className="flex">
                        <span className="btn-icon" onClick={() => onRemove(key)}>
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
